import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { AreaWaypointsMap } from "@ryde/controls";
import {
  DirectionEnum,
  IAreaWaypoint,
  ICoordinate,
  IGeoJSON,
  isAreaWaypointAlreadySelected,
  MAX_AREA_WAYPOINTS_PER_DIRECTION,
  TAreaWaypointDirection,
} from "@ryde/shared";

import { EditAreaWaypointsMapControls } from "./edit-area-waypoints-map-controls";
import { EditAreaWaypointsRow } from "./edit-area-waypoints.row";
import { EditAreaWaypointsDrawerBodyStyled } from "./edit-area-waypoints.styled";

interface IEditAreaWaypointsContentProps {
  waypoints: IAreaWaypoint[];
  direction: TAreaWaypointDirection;
  polygon?: IGeoJSON;
  areaName?: string;
  disabled?: boolean;
  onChange: (waypoints: IAreaWaypoint[]) => void;
}

export const EditAreaWaypointsContent: FC<IEditAreaWaypointsContentProps> = ({
  waypoints,
  direction,
  polygon,
  areaName,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const directionLabel =
    direction === DirectionEnum.DropOff
      ? t("area.formLabel.areaWaypointsDirectionB")
      : t("area.formLabel.areaWaypointsDirectionA");
  const listTitle = areaName
    ? `${areaName} (${directionLabel})`
    : directionLabel;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const sortableIds = useMemo(
    () => waypoints.map(waypoint => String(waypoint.id)),
    [waypoints],
  );

  const addWaypoint = useEvent((waypoint: IAreaWaypoint) => {
    if (
      waypoints.length >= MAX_AREA_WAYPOINTS_PER_DIRECTION ||
      isAreaWaypointAlreadySelected(waypoints, waypoint)
    ) {
      return;
    }

    onChange([...waypoints, waypoint]);
  });

  const handleDragEnd = useEvent((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = waypoints.findIndex(
      waypoint => String(waypoint.id) === String(active.id),
    );
    const newIndex = waypoints.findIndex(
      waypoint => String(waypoint.id) === String(over.id),
    );
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    onChange(arrayMove(waypoints, oldIndex, newIndex));
  });

  const handleNameChange = useEvent(
    ({ id, name }: { id: number; name: string }) => {
      onChange(
        waypoints.map(waypoint =>
          waypoint.id === id ? { ...waypoint, name } : waypoint,
        ),
      );
    },
  );

  const handleDelete = useEvent((id: number) => {
    onChange(waypoints.filter(waypoint => waypoint.id !== id));
  });

  const handleDragOnMap = useEvent(
    ({ id, lat, lng }: { id: number } & ICoordinate) => {
      onChange(
        waypoints.map(waypoint =>
          waypoint.id === id
            ? { ...waypoint, latitude: lat, longitude: lng }
            : waypoint,
        ),
      );
    },
  );

  return (
    <EditAreaWaypointsDrawerBodyStyled>
      <div className="edit-area-waypoints-list">
        <p className="edit-area-waypoints-list-title">{listTitle}</p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="edit-area-waypoints-rows">
            {waypoints.length === 0 && (
              <div className="edit-area-waypoints-empty-list">
                {t("area.formLabel.areaWaypointsEmpty")}
              </div>
            )}
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {waypoints.map(waypoint => (
                <EditAreaWaypointsRow
                  key={waypoint.id}
                  waypoint={waypoint}
                  disabled={disabled}
                  onNameChange={handleNameChange}
                  onDelete={handleDelete}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      </div>
      <AreaWaypointsMap
        className="edit-area-waypoints-map"
        direction={direction}
        waypoints={waypoints}
        polygon={polygon}
        onWaypointDragEnd={disabled ? undefined : handleDragOnMap}
      >
        {!disabled && (
          <EditAreaWaypointsMapControls
            waypoints={waypoints}
            onAddWaypoint={addWaypoint}
          />
        )}
      </AreaWaypointsMap>
    </EditAreaWaypointsDrawerBodyStyled>
  );
};
